import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { EczemaEntry } from '../types/eczema';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from './AuthContext';
import { useEncryption } from './EncryptionContext';
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import * as Sentry from '@sentry/react';
import { downloadEncryptedPayload, uploadEncryptedPayload } from '@/utils/encrypted-file-storage';
import { generateSticker } from '@/utils/sticker/generate-sticker';
import { stickersSupported } from '@/utils/sticker/sticker-support';
import { mapLoadedStickers } from '@/utils/sticker/sticker-load';

interface EczemaDataContextType {
  entries: EczemaEntry[];
  addEntry: (entry: EczemaEntry) => Promise<string | null>;
  getEntry: (id: string) => EczemaEntry | undefined;
  updateEntry: (id: string, updatedEntry: EczemaEntry, options?: { silent?: boolean }) => Promise<boolean>;
  deleteEntry: (id: string) => Promise<boolean>;
  isLoading: boolean;
}

const EczemaDataContext = createContext<EczemaDataContextType | undefined>(undefined);
const MIGRATION_BATCH_LIMIT = 3;
const MIGRATION_BATCH_BYTES = 250_000;
type EncryptedMigrationEntry = {
  id: string;
  date: string;
  notes: string;
  images?: string[];
  imageStorageIds?: string[];
  severityRatings?: EczemaEntry["severityRatings"] | string;
};

const estimateMigrationBatchBytes = (batch: EncryptedMigrationEntry[]) =>
  new TextEncoder().encode(JSON.stringify({ entries: batch })).length;

const buildMigrationBatches = (encryptedEntries: EncryptedMigrationEntry[]) => {
  const batches: EncryptedMigrationEntry[][] = [];
  let currentBatch: EncryptedMigrationEntry[] = [];

  for (const entry of encryptedEntries) {
    const candidate = [...currentBatch, entry];
    if (
      currentBatch.length > 0 &&
      (candidate.length > MIGRATION_BATCH_LIMIT || estimateMigrationBatchBytes(candidate) > MIGRATION_BATCH_BYTES)
    ) {
      batches.push(currentBatch);
      currentBatch = [entry];
    } else {
      currentBatch = candidate;
    }
  }

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
};

const arraysEqual = (a?: (string | null)[], b?: (string | null)[]) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
};

export const useEczemaData = () => {
  const context = useContext(EczemaDataContext);
  if (context === undefined) {
    throw new Error('useEczemaData must be used within a EczemaDataProvider');
  }
  return context;
};

const DEV_BYPASS = import.meta.env.DEV && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('bypass-auth');

export const EczemaDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { status: encryptionStatus, encryptEntry: encryptEntryFn, decryptEntry: decryptEntryFn } = useEncryption();

  // Query entries if user is logged in; skip entirely in dev bypass mode
  const convexEntries = useQuery(api.entries.getEntries, (!DEV_BYPASS && user) ? {} : "skip");

  const addEntryMutation = useMutation(api.entries.addEntry);
  const updateEntryMutation = useMutation(api.entries.updateEntry);
  const deleteEntryMutation = useMutation(api.entries.deleteEntry);
  const batchMigrateMutation = useMutation(api.entries.batchMigrateEntries);
  const generateUploadUrlMutation = useMutation(api.entries.generateUploadUrl);

  // Decrypted entries state
  const [entries, setEntries] = useState<EczemaEntry[]>([]);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const storagePayloadCacheRef = useRef<Map<string, Promise<string>>>(new Map());

  const getStoredEncryptedPayload = async (storageId: string, url: string | null | undefined) => {
    if (!url) {
      throw new Error(`Missing download URL for storage file ${storageId}`);
    }

    const cached = storagePayloadCacheRef.current.get(storageId);
    if (cached) {
      return await cached;
    }

    const request = downloadEncryptedPayload(url).catch((error) => {
      storagePayloadCacheRef.current.delete(storageId);
      throw error;
    });
    storagePayloadCacheRef.current.set(storageId, request);
    return await request;
  };

  const loadEncryptedImagesFromStorage = async (
    storageIds?: string[],
    imageUrls?: Array<string | null>
  ) => {
    if (!storageIds || storageIds.length === 0) {
      return undefined;
    }

    return await Promise.all(
      storageIds.map(async (storageId, index) => {
        return await getStoredEncryptedPayload(storageId, imageUrls?.[index]);
      })
    );
  };

  const uploadEncryptedImagesToStorage = async (ciphertexts?: string[]) => {
    if (!ciphertexts || ciphertexts.length === 0) {
      return undefined;
    }

    return await Promise.all(
      ciphertexts.map(async (ciphertext) => {
        const uploadUrl = await generateUploadUrlMutation({});
        return await uploadEncryptedPayload(uploadUrl, ciphertext);
      })
    );
  };

  // Decrypt entries after fetching
  useEffect(() => {
    if (!convexEntries || encryptionStatus !== 'ready') return;

    const decryptAll = async () => {
      setIsDecrypting(true);
      try {
        const results = await Promise.all(
          convexEntries.map(async (e: any) => {
            try {
              if (e.encrypted) {
                const encryptedImages =
                  e.imageStorageIds && e.imageStorageIds.length > 0
                    ? await loadEncryptedImagesFromStorage(e.imageStorageIds, e.imageUrls)
                    : e.images;
                const encryptedStickers =
                  e.stickerStorageIds && e.stickerStorageIds.length > 0
                    ? await loadEncryptedImagesFromStorage(e.stickerStorageIds, e.stickerUrls)
                    : e.stickerImages;
                const raw = await decryptEntryFn({
                  date: e.date,
                  notes: e.notes,
                  images: encryptedImages,
                  stickerImages: encryptedStickers,
                  severityRatings: e.severityRatings,
                });
                // Re-expand dense stickers + pass per-image metadata through verbatim.
                // imageStickerable MUST stay undefined when unprocessed so the
                // generation effect picks the entry up (see mapLoadedStickers).
                const { stickerImages, imageStickerable, imageDisplayModes } =
                  mapLoadedStickers(
                    raw.images,
                    raw.stickerImages,
                    e.imageStickerable,
                    e.imageDisplayModes,
                  );
                return {
                  id: e._id,
                  date: raw.date,
                  notes: raw.notes,
                  images: raw.images,
                  stickerImages,
                  imageDisplayModes,
                  imageStickerable,
                  severityRatings: raw.severityRatings,
                };
              }
              // Legacy plaintext entry
              return {
                id: e._id,
                date: e.date,
                notes: e.notes,
                images: e.images,
                stickerImages: e.stickerImages,
                imageDisplayModes: e.imageDisplayModes,
                imageStickerable: e.imageStickerable,
                severityRatings: e.severityRatings,
              };
            } catch (entryError) {
              console.error(`Failed to decrypt entry ${e._id}:`, entryError);
              Sentry.captureException(entryError, {
                tags: { component: 'EczemaDataContext', action: 'decrypt' },
                extra: { entryId: e._id },
              });
              return null;
            }
          })
        );

        const decrypted = results.filter((e): e is EczemaEntry => e !== null);

        if (decrypted.length < convexEntries.length) {
          toast({
            title: 'Decryption error',
            description: `${convexEntries.length - decrypted.length} entry(s) could not be decrypted.`,
            variant: 'destructive',
          });
        }

        setEntries(decrypted);
      } finally {
        setIsDecrypting(false);
      }
    };

    decryptAll();
  }, [convexEntries, encryptionStatus, decryptEntryFn]);

  // Sticker generation: process entries whose images haven't been sticker-processed yet
  const processingStickersRef = useRef<Set<string>>(new Set());
  const stickerFailedRef = useRef<Set<string>>(new Set());
  // Entries that already existed (without stickers) the first time data loaded:
  // the "pre-sticker era" backlog. We deliberately leave these as-is and never
  // auto-convert them — only entries created/edited after this baseline get
  // stickers. Captured once, the first time entries are ready (see the backfill
  // effect). Without this, opening the Logs tab would bulk-process the entire
  // history through single-threaded WASM and freeze the UI.
  const stickerBaselineRef = useRef<Set<string> | null>(null);

  // Migration: re-encrypt legacy plaintext entries
  const migrationRanRef = useRef(false);
  const migrationUserIdRef = useRef<string | null>(null);
  const migratingIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!convexEntries || encryptionStatus !== 'ready' || !user) return;

    // Reset migration flag when user changes
    if (migrationUserIdRef.current !== user.id) {
      migrationRanRef.current = false;
      migrationUserIdRef.current = user.id;
      migratingIdsRef.current = new Set();
    }

    if (migrationRanRef.current) return;
    migrationRanRef.current = true;

    const legacyEntries = convexEntries.filter((e: any) => !e.encrypted);
    if (legacyEntries.length === 0) return;

    // Migrate in background — chunk payloads so large encrypted entries don't overflow one mutation.
    (async () => {
      const entriesToMigrate = legacyEntries.filter(
        (e: any) => !migratingIdsRef.current.has(e._id)
      );
      if (entriesToMigrate.length === 0) return;

      // Mark all as migrating (guards against StrictMode double-runs)
      for (const e of entriesToMigrate) {
        migratingIdsRef.current.add(e._id);
      }

      try {
        const encryptedEntries = await Promise.all(
          entriesToMigrate.map(async (e: any) => {
            const encrypted = await encryptEntryFn({
              date: e.date,
              notes: e.notes,
              images: e.images,
              severityRatings: e.severityRatings,
            });
            const imageStorageIds = await uploadEncryptedImagesToStorage(encrypted.images);
            return {
              id: e._id,
              date: encrypted.date,
              notes: encrypted.notes,
              images: undefined,
              imageStorageIds,
              severityRatings: encrypted.severityRatings,
            };
          })
        );

        const batches = buildMigrationBatches(encryptedEntries);

        for (const batch of batches) {
          try {
            await batchMigrateMutation({ entries: batch });
          } catch (batchError) {
            // Fall back to single-entry updates for oversized or partially failing batches.
            for (const entry of batch) {
              await updateEntryMutation({
                id: entry.id as any,
                date: entry.date,
                notes: entry.notes,
                images: entry.images,
                imageStorageIds: entry.imageStorageIds as any,
                severityRatings: entry.severityRatings,
                encrypted: true,
              });
            }
          }
        }

        const encryptedInlineImageEntries = convexEntries.filter(
          (e: any) =>
            e.encrypted &&
            Array.isArray(e.images) &&
            e.images.length > 0 &&
            (!e.imageStorageIds || e.imageStorageIds.length === 0) &&
            !migratingIdsRef.current.has(e._id)
        );

        for (const entry of encryptedInlineImageEntries) {
          migratingIdsRef.current.add(entry._id);
          const imageStorageIds = await uploadEncryptedImagesToStorage(entry.images);
          await updateEntryMutation({
            id: entry._id,
            date: entry.date,
            notes: entry.notes,
            images: undefined,
            imageStorageIds: imageStorageIds as any,
            severityRatings: entry.severityRatings,
            encrypted: true,
          });
        }
      } catch (error) {
        // Clear migrating flags so retry is possible
        for (const e of entriesToMigrate) {
          migratingIdsRef.current.delete(e._id);
        }
        console.error('Batch migration failed:', error);
        Sentry.captureException(error, {
          tags: { component: 'EczemaDataContext', action: 'migration' },
          extra: { entryCount: entriesToMigrate.length },
        });
      }
    })();
  }, [convexEntries, encryptionStatus, user, encryptEntryFn, batchMigrateMutation, updateEntryMutation]);

  const addEntry = async (entry: EczemaEntry) => {
    if (!user) return null;
    try {
      const entryImages = entry.images && entry.images.length > 0 ? entry.images : undefined;
      // Build dense list of non-null stickers, in image order.
      const stickerEntries = (entry.stickerImages ?? [])
        .map((s, i) => ({ s, i }))
        .filter((x) => x.s != null) as { s: string; i: number }[];
      const encrypted = await encryptEntryFn({
        date: entry.date,
        notes: entry.notes,
        images: entryImages,
        stickerImages: stickerEntries.length ? stickerEntries.map((x) => x.s) : undefined,
        severityRatings: entry.severityRatings,
      });
      const imageStorageIds = await uploadEncryptedImagesToStorage(encrypted.images);
      const stickerStorageIds = await uploadEncryptedImagesToStorage(encrypted.stickerImages);
      const entryId = await addEntryMutation({
        date: encrypted.date,
        notes: encrypted.notes,
        images: undefined,
        imageStorageIds: imageStorageIds as any,
        stickerStorageIds: stickerStorageIds as any,
        imageDisplayModes: entry.imageDisplayModes,
        imageStickerable: entry.imageStickerable?.map((b) => b ?? false),
        severityRatings: encrypted.severityRatings,
        encrypted: true,
      });
      toast({
        title: 'Entry added',
        description: `Your entry for ${new Date(entry.date).toLocaleDateString()} has been saved.`,
      });
      return entryId as string;
    } catch (error) {
      console.error('addEntry failed:', error);
      Sentry.captureException(error, {
        tags: { component: 'EczemaDataContext', action: 'addEntry' },
        extra: { hasImages: Boolean(entry.images?.length) },
      });
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save entry.',
        variant: 'destructive',
      });
      return null;
    }
  };

  const getEntry = (id: string) => {
    return entries.find((entry) => entry.id === id);
  };

  const updateEntry = async (
    id: string,
    updatedEntry: EczemaEntry,
    options?: { silent?: boolean }
  ) => {
    if (!user) return false;
    try {
      const existingRawEntry = convexEntries?.find((entry: any) => entry._id === id);
      const existingEntry = entries.find((entry) => entry.id === id);
      const shouldReuseStoredImages =
        Boolean(existingRawEntry?.imageStorageIds?.length) &&
        arraysEqual(existingEntry?.images, updatedEntry.images);
      const shouldReuseStoredStickers =
        Boolean(existingRawEntry?.stickerStorageIds?.length) &&
        arraysEqual(existingEntry?.stickerImages, updatedEntry.stickerImages);

      // Build dense list of non-null stickers, in image order.
      const stickerEntries = (updatedEntry.stickerImages ?? [])
        .map((s, i) => ({ s, i }))
        .filter((x) => x.s != null) as { s: string; i: number }[];

      const encrypted = await encryptEntryFn({
        date: updatedEntry.date,
        notes: updatedEntry.notes,
        images: shouldReuseStoredImages ? undefined : updatedEntry.images,
        stickerImages: shouldReuseStoredStickers
          ? undefined
          : stickerEntries.length
            ? stickerEntries.map((x) => x.s)
            : undefined,
        severityRatings: updatedEntry.severityRatings,
      });
      const imageStorageIds = shouldReuseStoredImages
        ? existingRawEntry.imageStorageIds
        : await uploadEncryptedImagesToStorage(encrypted.images);
      const stickerStorageIds = shouldReuseStoredStickers
        ? existingRawEntry.stickerStorageIds
        : await uploadEncryptedImagesToStorage(encrypted.stickerImages);
      await updateEntryMutation({
        id: id as any,
        date: encrypted.date,
        notes: encrypted.notes,
        images: undefined,
        imageStorageIds: imageStorageIds as any,
        stickerStorageIds: stickerStorageIds as any,
        imageDisplayModes: updatedEntry.imageDisplayModes,
        imageStickerable: updatedEntry.imageStickerable?.map((b) => b ?? false),
        severityRatings: encrypted.severityRatings,
        encrypted: true,
      });
      if (!options?.silent) {
        toast({
          title: 'Entry updated',
          description: `Your entry for ${new Date(updatedEntry.date).toLocaleDateString()} has been updated.`,
        });
      }
      return true;
    } catch (error) {
      console.error('updateEntry failed:', error);
      Sentry.captureException(error, {
        tags: { component: 'EczemaDataContext', action: 'updateEntry' },
        extra: { entryId: id, hasImages: Boolean(updatedEntry.images?.length) },
      });
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update entry.',
        variant: 'destructive',
      });
      return false;
    }
  };

  // Background: generate stickers for NEW entries whose images haven't been
  // processed. Pre-sticker-era entries (everything that already existed when data
  // first loaded) are left as-is — see stickerBaselineRef.
  useEffect(() => {
    if (encryptionStatus !== 'ready') return;
    // On-device generation crashes the native iOS WebView (memory); web only.
    if (!stickersSupported()) return;

    // Wait until the initial query + decrypt has settled before snapshotting the
    // backlog — capturing an empty/partial set early would let the real backlog
    // arrive later as "new" and get bulk-processed (the freeze we're fixing).
    const entriesLoaded = (DEV_BYPASS || convexEntries !== undefined) && !isDecrypting;
    if (!entriesLoaded) return;

    // Capture the pre-sticker backlog once: every entry present the first time
    // data is ready is "old" and excluded from auto-conversion. Entries created
    // or edited after this (new IDs / reset sticker fields) are NOT in the
    // baseline, so they still get stickers.
    if (stickerBaselineRef.current === null) {
      stickerBaselineRef.current = new Set(entries.map((e) => e.id));
      return; // nothing to backfill on the baseline pass
    }

    const pending = entries.filter(
      (e) =>
        e.images && e.images.length > 0 &&
        e.imageStickerable === undefined &&
        !stickerBaselineRef.current!.has(e.id) &&
        !processingStickersRef.current.has(e.id) &&
        !stickerFailedRef.current.has(e.id)
    );
    if (pending.length === 0) return;

    // Synchronously mark ALL pending entries before any await, closing the
    // StrictMode double-invoke / dependency-change race window.
    for (const entry of pending) processingStickersRef.current.add(entry.id);

    (async () => {
      for (const entry of pending) {
        try {
          const results = await Promise.all(
            (entry.images ?? []).map((img) => generateSticker(img))
          );
          const imageStickerable = results.map((r) => r.stickerable);
          const stickerImages = results.map((r) => r.sticker);
          const defaultMode = user?.imageDisplayDefault ?? 'sticker';
          const imageDisplayModes = results.map((r) =>
            defaultMode === 'sticker' && r.stickerable ? 'sticker' : 'normal'
          ) as ('normal' | 'sticker')[];
          await updateEntry(
            entry.id,
            { ...entry, stickerImages, imageStickerable, imageDisplayModes },
            { silent: true }
          );
        } catch (err) {
          console.error('Sticker generation failed for', entry.id, err);
          stickerFailedRef.current.add(entry.id);
        } finally {
          processingStickersRef.current.delete(entry.id);
        }
      }
    })();
  }, [entries, encryptionStatus, updateEntry, convexEntries, isDecrypting]);

  const deleteEntry = async (id: string) => {
    if (!user) return false;
    try {
      await deleteEntryMutation({
        id: id as any,
      });
      toast({
        title: 'Entry deleted',
        description: 'Your entry has been deleted.',
      });
      return true;
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error',
        description: 'Failed to delete entry.',
        variant: 'destructive',
      });
      return false;
    }
  };

  const isInitialLoad = !DEV_BYPASS && convexEntries === undefined && !!user;
  const isEncryptionBootstrap = encryptionStatus === 'loading' && !!user;
  const isInitialDecrypt = isDecrypting && entries.length === 0;

  const value = {
    entries,
    addEntry,
    getEntry,
    updateEntry,
    deleteEntry,
    isLoading: isInitialLoad || isInitialDecrypt || isEncryptionBootstrap,
  };

  return (
    <EczemaDataContext.Provider value={value}>
      {children}
    </EczemaDataContext.Provider>
  );
};
